import React, { useMemo, useState } from 'react';
import {
  Drawer,
  Collapse,
  Input,
  List,
  Button,
  Typography,
} from 'antd';
import MetricLabel, { MetricDescription, getMetricDescription, getMetricLabel } from 'components/MetricLabel';
import useFormatMessage from 'hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import standardMetrics from 'shared/constants/metrics';

const METRIC_CATEGORY_LABELS = {
  visitors: 'Visitors',
  behavior: 'Behavior',
  siteSearch: 'Site search',
  content: 'Content',
  engagement: 'Engagement',
  conversion: 'Conversion',
  performance: 'Performance',
  survey: 'Survey',
  other: 'Other',
};

const MetricPickerDrawer = ({
  open,
  mode,
  selectedMetric,
  onClose,
  onSelectMetric,
}) => {
  const $t = useFormatMessage();
  const { isSuperUser } = useUser();
  const [metricSearch, setMetricSearch] = useState('');
  const [activeMetricCategories, setActiveMetricCategories] = useState([]);

  const groupedMetricOptions = useMemo(() => {
    const groupedMetrics = Object.entries(standardMetrics).reduce((acc, [metric, metricConfig]) => {
      const category = metricConfig.category || 'visitors';
      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push({
        value: metric,
        label: getMetricLabel({ $t, metric }),
        description: getMetricDescription({ $t, metric }) || '',
      });

      return acc;
    }, {});

    return Object.entries(groupedMetrics)
      .sort(([a], [b]) => {
        const aIndex = Object.keys(METRIC_CATEGORY_LABELS).indexOf(a);
        const bIndex = Object.keys(METRIC_CATEGORY_LABELS).indexOf(b);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
      })
      .map(([category, options]) => ({
        category,
        label: METRIC_CATEGORY_LABELS[category] || category,
        options,
      }));
  }, [$t]);

  const filteredMetricGroups = useMemo(() => {
    const searchValue = metricSearch.trim().toLowerCase();

    if (!searchValue) {
      return groupedMetricOptions;
    }

    return groupedMetricOptions
      .map(group => ({
        ...group,
        options: group.options.filter(option => (
          option.label.toLowerCase().includes(searchValue)
          || option.description.toLowerCase().includes(searchValue)
        )),
      }))
      .filter(group => group.options.length > 0);
  }, [groupedMetricOptions, metricSearch]);

  const metricSections = useMemo(() => {
    const sections = filteredMetricGroups.map(group => ({
      key: `category:${group.category}`,
      label: group.label,
      options: group.options,
    }));

    return sections;
  }, [filteredMetricGroups, isSuperUser, metricSearch]);

  const collapseActiveKeys = metricSearch.trim()
    ? metricSections.map(section => section.key)
    : activeMetricCategories;

  const closeDrawer = () => {
    setMetricSearch('');
    setActiveMetricCategories([]);
    onClose();
  };

  return (
    <Drawer
      title={mode === 'change' ? 'Change metric' : 'Add metric'}
      open={open}
      onClose={closeDrawer}
      width={640}
    >
      <Input
        value={metricSearch}
        onChange={event => setMetricSearch(event.target.value)}
        placeholder="Search metrics"
        style={{ marginBottom: 16 }}
      />
      {metricSections.length > 0 ? (
        <Collapse
          activeKey={collapseActiveKeys}
          onChange={keys => setActiveMetricCategories(Array.isArray(keys) ? keys : [keys])}
          items={metricSections.map(section => ({
            key: section.key,
            label: section.label,
            children: (
              <List
                split
                dataSource={section.options}
                renderItem={(option) => {
                  const isSelected = selectedMetric === option.value;
                  const title = <MetricLabel metric={option.value} lineBreaks={false} />;
                  const description = <MetricDescription metric={option.value} />;

                  return (
                    <List.Item
                      onClick={() => onSelectMetric(option.value)}
                      style={{ cursor: 'pointer' }}
                      extra={(
                        <Button
                          type={isSelected ? 'primary' : 'default'}
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectMetric(option.value);
                          }}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                      )}
                    >
                      <List.Item.Meta
                        title={title}
                        description={description}
                      />
                    </List.Item>
                  );
                }}
              />
            ),
          }))}
        />
      ) : (
        <Typography.Text type="secondary">
          No metrics found for this search
        </Typography.Text>
      )}
    </Drawer>
  );
};

export default MetricPickerDrawer;
